import React, { useState, useCallback, useEffect } from "react";
import "@/App.css";

// Components
import Navigation from "./components/Navigation";
import HeroSection from "./components/HeroSection";
import HowItWorksSection from "./components/HowItWorksSection";
import ProductModulesSection from "./components/ProductModulesSection";
import EvidenceBasedSection from "./components/EvidenceBasedSection";
import ROISection from "./components/ROISection";
import ROICalculator from "./components/ROICalculator";
import ROICalculatorIndia from "./components/ROICalculatorIndia";
import InteractiveLiveSimulation from "./components/InteractiveLiveSimulation";
import DRDMESection from "./components/DRDMESection";
import ComplianceSection from "./components/ComplianceSection";
import ClinicianAccessSection from "./components/ClinicianAccessSection";
import FooterCTA from "./components/FooterCTA";
import DemoModal from "./components/DemoModal";
import AdminPage from "./components/AdminPage";

function App() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    // Check localStorage or system preference
    const saved = localStorage.getItem('skydoc-theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Check for admin route
  useEffect(() => {
    if (window.location.pathname === '/admin' || window.location.hash === '#admin') {
      setShowAdmin(true);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('skydoc-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const handleThemeToggle = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  const handleDemoClick = useCallback(() => {
    setIsDemoModalOpen(true);
  }, []);

  const handleCloseDemoModal = useCallback(() => {
    setIsDemoModalOpen(false);
  }, []);

  const scrollToSimulation = useCallback(() => {
    const element = document.querySelector('#simulation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Show admin page
  if (showAdmin) {
    return <AdminPage onBack={() => {
      setShowAdmin(false);
      window.history.pushState({}, '', '/');
    }} />;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-900' : 'bg-[#FAFAF9]'}`} data-testid="skydoc-app">
      {/* Navigation */}
      <Navigation 
        onDemoClick={handleDemoClick} 
        isDark={isDark}
        onThemeToggle={handleThemeToggle}
      />

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <HeroSection 
          onDemoClick={handleDemoClick} 
          onWatchDemo={scrollToSimulation}
          isDark={isDark}
        />

        {/* How It Works */}
        <HowItWorksSection isDark={isDark} />

        {/* Product Modules */}
        <ProductModulesSection isDark={isDark} />

        {/* Evidence-Based Practice */}
        <EvidenceBasedSection isDark={isDark} />

        {/* ROI Section */}
        <ROISection isDark={isDark} />

        {/* ROI Calculator */}
        <ROICalculator isDark={isDark} />

        {/* India ROI Calculator */}
        <ROICalculatorIndia isDark={isDark} />

        {/* DR & DME Precision Care */}
        <DRDMESection isDark={isDark} onDemoClick={handleDemoClick} />

        {/* Interactive Live Simulation */}
        <InteractiveLiveSimulation isDark={isDark} />

        {/* Compliance & Trust */}
        <ComplianceSection isDark={isDark} />

        {/* Clinician Access Portal */}
        <ClinicianAccessSection isDark={isDark} />

        {/* Footer CTA */}
        <FooterCTA onDemoClick={handleDemoClick} isDark={isDark} />
      </main>

      {/* Demo Request Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={handleCloseDemoModal}
        isDark={isDark}
      />
    </div>
  );
}

export default App;
